import jwt_decode from "jwt-decode";
import { computed, shallowRef, watch, watchEffect } from "vue";
import {
    APIResponse,
    EnumUserRole,
    SessionDto,
} from "@/shared/api-contract/api-contract";
import { appAxios } from "@/core/utils/appAxios";
import { JWTTokenPayload, JWTTokenPayloadUser } from "../models/types/JWTTokenPayload";

const STORAGE_SESSIONDTO_KEY = "sessionDto";

function decodeToken(token: string) {
    try {
        return jwt_decode(token);
    } catch {
        throw new Error("Can't decode token: " + token);
    }
}

export class SessionModel {
    sessionDto = shallowRef<SessionDto | undefined>();

    JWTUserDto = shallowRef<JWTTokenPayloadUser | undefined>();

    get isViewer(): boolean {
        return (
            !this.JWTUserDto.value ||
            this.JWTUserDto.value?.id_role === EnumUserRole.VIEWER
        );
    }
    payload = computed<undefined | JWTTokenPayload>(() => {
        if (!this.sessionDto.value) {
            return undefined;
        }

        return jwt_decode<JWTTokenPayload>(this.sessionDto.value.token);
    });

    tokenRefreshTimeout!: NodeJS.Timeout;

    constructor() {
        const sessionDto = this.getSessionDto();
        if (sessionDto) {
            this.setSessionDto(sessionDto);
        }
    }

    scheduleSessionRefresh() {
        clearTimeout(this.tokenRefreshTimeout);
        const token = this.sessionDto.value?.token;
        if (!token) {
            return;
        }

        this.tokenRefreshTimeout = setTimeout(
            () => this.refreshSession(),
            this.getTokenExpirationTime(token).getTime() - Date.now() - 60 * 1000,
        );
    }

    private getSessionDto(): SessionDto | undefined {
        const sessionDto = localStorage.getItem(STORAGE_SESSIONDTO_KEY);
        if (sessionDto !== null) {
            return JSON.parse(sessionDto as string) as SessionDto;
        }
    }

    logout() {
        this.sessionDto.value = undefined;
        this.JWTUserDto.value = undefined;
        localStorage.removeItem(STORAGE_SESSIONDTO_KEY);
    }

    setSessionDto(sessionDto: SessionDto) {
        this.sessionDto.value = sessionDto;
        localStorage.setItem(
            STORAGE_SESSIONDTO_KEY,
            JSON.stringify(this.sessionDto.value),
        );
        if (this.payload.value) {
            this.JWTUserDto.value = {
                email_verified: this.payload.value.email_verified,
                name: this.payload.value.name,
                preferred_username: this.payload.value.preferred_username,
                given_name: this.payload.value.given_name,
                family_name: this.payload.value.family_name,
                email: this.payload.value.email,
                verified_user: this.payload.value.verified_user,
                id_customer: this.payload.value.id_customer,
                id_role: this.payload.value.id_role,
                id_contact: this.payload.value.id_contact,
                scope: this.payload.value.scope,
            };
        }
        this.scheduleSessionRefresh();
    }

    async refreshSession() {
        if (!this.sessionDto.value) {
            throw new Error("SessionDto shouldn't be empty");
        }

        const refreshToken = this.sessionDto.value?.refresh_token;

        if (!refreshToken) {
            throw new Error("refreshToken shouldn't be empty");
        }

        if (this.getTokenExpirationTime(refreshToken).getTime() <= Date.now()) {
            this.logout();
            throw new Error( // TODO replace error to logout
                "jwt refresh token is expired, expire date is " +
                    this.getTokenExpirationTime(refreshToken),
            );
        }

        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const newSessionDto = await refreshSession(refreshToken);

        if (!newSessionDto) {
            this.logout();

            return;
        }

        this.setSessionDto(newSessionDto);
    }

    getTokenExpirationTime(jwtToken: string): Date {
        const jwtData = decodeToken(jwtToken) as Record<string, unknown>;
        if (!("exp" in jwtData)) {
            throw new Error("exp not found in jwt data");
        }

        return new Date((jwtData.exp as number) * 1000);
    }
}

export const sessionModel = new SessionModel();

watch(
    () => ({
        payload: sessionModel.payload.value,
    }),
    ({ payload }) => {
        if (!payload) return;
        sessionModel.JWTUserDto.value = {
            email_verified: payload.email_verified,
            name: payload.name,
            preferred_username: payload.preferred_username,
            given_name: payload.given_name,
            family_name: payload.family_name,
            email: payload.email,
            verified_user: payload.verified_user,
            id_customer: payload.id_customer,
            id_role: payload.id_role,
            id_contact: payload.id_contact,
            scope: payload.scope,
        };
    },
);
watchEffect(() => {
    if (sessionModel.sessionDto.value && sessionModel.sessionDto.value?.token) {
        console.log(
            "token changed",
            sessionModel.payload,
            new Date(
                (
                    decodeToken(sessionModel.sessionDto.value?.token + "") as {
                        exp: number;
                    }
                ).exp * 1000,
            ),
            new Date(
                (
                    decodeToken(sessionModel.sessionDto.value?.refresh_token + "") as {
                        exp: number;
                    }
                ).exp * 1000,
            ),
        );
    }
});

async function refreshSession(refreshToken: string): Promise<SessionDto | null> {
    const response = await appAxios.value.post<APIResponse<SessionDto>>(
        `/client/web/auth/login/refresh`,
        undefined,
        {
            headers: {
                Authorization: `Bearer ${refreshToken}`,
                "Accept-Language": "en",
                "Content-Type": "application/x-www-form-urlencoded",
                accept: "application/json",
            },
        },
    );

    if (response.status !== 200) {
        return null;
    }

    return response.data.data;
}
